import React, { useState, useMemo } from 'react';
import { FaCalculator, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { getCurrencyDisplayName } from '../utils/currencyHelper';

// Yardımcı fonksiyon
const getTypeName = (type) => {
    return getCurrencyDisplayName(type);
};

const ProfitSimulator = ({ portfolio }) => {
    const [targetPrices, setTargetPrices] = useState({});

    // Benzersiz varlık tiplerini ve mevcut fiyatlarını al
    const uniqueAssets = useMemo(() => {
        if (!portfolio || portfolio.length === 0) return [];

        const assetMap = {};
        portfolio.forEach(item => {
            if (!assetMap[item.type]) {
                assetMap[item.type] = {
                    type: item.type,
                    name: getTypeName(item.type),
                    currentPrice: item.currentPrice,
                    totalAmount: 0,
                    totalCost: 0
                };
            }
            assetMap[item.type].totalAmount += item.amount;
            assetMap[item.type].totalCost += item.totalCost;
        });

        return Object.values(assetMap);
    }, [portfolio]);

    // İlk yüklemede hedef fiyatları mevcut fiyatlara eşitle
    React.useEffect(() => {
        const initialPrices = {};
        uniqueAssets.forEach(asset => {
            initialPrices[asset.type] = asset.currentPrice;
        });
        setTargetPrices(initialPrices);
    }, [uniqueAssets]);

    // Simülasyon sonuçlarını hesapla
    const simulationResults = useMemo(() => {
        let projectedTotal = 0;
        let currentTotal = 0;

        uniqueAssets.forEach(asset => {
            const targetPrice = targetPrices[asset.type] || asset.currentPrice;
            projectedTotal += asset.totalAmount * targetPrice;
            currentTotal += asset.totalAmount * asset.currentPrice;
        });

        const totalCost = uniqueAssets.reduce((sum, a) => sum + a.totalCost, 0);
        const projectedProfit = projectedTotal - totalCost;
        const projectedProfitPercent = totalCost > 0 ? ((projectedProfit / totalCost) * 100) : 0;

        return {
            projectedTotal,
            currentTotal,
            projectedProfit,
            projectedProfitPercent,
            totalCost
        };
    }, [uniqueAssets, targetPrices]);

    const handlePriceChange = (type, value) => {
        setTargetPrices(prev => ({
            ...prev,
            [type]: parseFloat(value) || 0
        }));
    };

    if (!portfolio || portfolio.length === 0) {
        return null;
    }

    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-gray-800 dark:to-gray-700 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-3">
                    <div className="bg-purple-100 dark:bg-purple-900 p-3 rounded-lg">
                        <FaCalculator className="text-purple-600 dark:text-purple-400 text-xl" />
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">Hedef Simülatörü</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">"Kurlar şu olursa..." senaryoları</p>
                    </div>
                </div>
            </div>

            {/* Content - Always visible */}
            <div className="p-6 space-y-6">
                {/* Target Price Inputs */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {uniqueAssets.map(asset => (
                        <div key={asset.type} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 transition-colors hover:bg-gray-100 dark:hover:bg-gray-600">
                            <div className="flex justify-between items-center mb-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                                    {asset.name}
                                </label>
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 px-2 py-1 rounded border border-gray-200 dark:border-gray-600">
                                    Mevcut: ₺{asset.currentPrice.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                                </span>
                            </div>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <span className="text-gray-500 dark:text-gray-400">₺</span>
                                </div>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={targetPrices[asset.type] || ''}
                                    onChange={(e) => handlePriceChange(asset.type, e.target.value)}
                                    className="block w-full pl-8 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg py-2.5 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                    placeholder={asset.currentPrice.toFixed(2)}
                                />
                            </div>
                        </div>
                    ))}
                </div>

                {/* Results */}
                <div className="bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900/30 dark:to-blue-900/30 rounded-xl p-6">
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                        <FaCalculator className="mr-2 text-purple-600 dark:text-purple-400" />
                        Simülasyon Sonuçları
                    </h4>

                    <div className="grid grid-cols-3 gap-2">
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center shadow-sm">
                            <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1 font-semibold leading-tight">Tahmini Portföy Değeri</p>
                            <p className="text-sm md:text-lg font-extrabold text-gray-900 dark:text-white tracking-tight">
                                ₺{simulationResults.projectedTotal.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                            </p>
                        </div>

                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center shadow-sm">
                            <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1 font-semibold leading-tight">Tahmini Kar/Zarar</p>
                            <div className={`flex items-center justify-center space-x-1 ${simulationResults.projectedProfit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                {simulationResults.projectedProfit >= 0 ? <FaArrowUp className="text-xs" /> : <FaArrowDown className="text-xs" />}
                                <p className="text-sm md:text-lg font-extrabold tracking-tight">
                                    {simulationResults.projectedProfit >= 0 ? '+' : ''}₺{Math.abs(simulationResults.projectedProfit).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                </p>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 text-center shadow-sm">
                            <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1 font-semibold leading-tight">Tahmini Getiri</p>
                            <p className={`text-sm md:text-lg font-extrabold tracking-tight ${simulationResults.projectedProfitPercent >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                {simulationResults.projectedProfitPercent >= 0 ? '+' : ''}{simulationResults.projectedProfitPercent.toFixed(2)}%
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfitSimulator;
